import { useQuery } from '@tanstack/react-query';

import { type LabelingStatsOut, fetchLabelingStats } from '@/api/labeling';
import { Card, CardContent } from '@/components/ui/card';
import { ProgressBar } from '@/components/ui/progress';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { useAuth } from '../auth/AuthContext';

const STATS_KEY = ['labeling', 'stats'] as const;

const pct = (rate: number) => `${Math.round(rate * 100)}%`;

/** Corpus progress dashboard: labeled vs total, per-field coverage,
 *  contributors, and inter-annotator agreement (INP-652). Read-only. */
export function LabelingStatsPanel() {
  const { getIdToken } = useAuth();
  const query = useQuery({
    queryKey: STATS_KEY,
    queryFn: async () => fetchLabelingStats((await getIdToken()) ?? ''),
  });

  if (query.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
          <Spinner /> Loading corpus stats…
        </CardContent>
      </Card>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-destructive">
          Couldn&apos;t load corpus stats.
        </CardContent>
      </Card>
    );
  }
  return <StatsBody stats={query.data} />;
}

function StatsBody({ stats }: { stats: LabelingStatsOut }) {
  const agreementByField = new Map(stats.agreement.map((a) => [a.field, a]));
  return (
    <Card>
      <CardContent className="flex flex-col gap-5 pt-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-brand-navy">Corpus progress</h2>
            <span className="text-xs text-brand-slate">
              {stats.labeled} / {stats.total_documents} labeled · {stats.labels_total} labels
            </span>
          </div>
          <ProgressBar done={stats.labeled} total={stats.total_documents} fullWidth />
          <p className="text-xs text-muted-foreground">
            {stats.pending} pending · {stats.skipped} skipped · {stats.unreadable} unreadable
          </p>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <div className="flex flex-col gap-2">
            <h3 className="text-xs font-medium uppercase tracking-wide text-brand-slate/70">
              Field coverage{stats.docs_with_multiple_labels > 0 ? ' · agreement' : ''}
            </h3>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Field</TableHead>
                    <TableHead className="text-right">Coverage</TableHead>
                    <TableHead className="text-right">Agreement</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.coverage.map((c) => {
                    const agree = agreementByField.get(c.field);
                    return (
                      <TableRow key={c.field}>
                        <TableCell className="text-brand-slate">{c.field}</TableCell>
                        <TableCell className="text-right tabular-nums text-brand-navy">
                          {pct(c.rate)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-brand-slate/70">
                          {agree ? `${pct(agree.rate)} agree` : ''}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <h3 className="text-xs font-medium uppercase tracking-wide text-brand-slate/70">
              Contributors
            </h3>
            {stats.contributors.length === 0 ? (
              <p className="text-sm text-muted-foreground">No labels yet.</p>
            ) : (
              <ul className="flex flex-col gap-1 text-sm">
                {stats.contributors.map((c) => (
                  <li
                    key={c.labeler}
                    className="flex items-center justify-between border-b border-border/60 py-1 last:border-0"
                  >
                    <span className="truncate text-brand-slate" title={c.labeler}>
                      {c.labeler}
                    </span>
                    <span className="tabular-nums text-brand-navy">{c.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
